// TrackedCategories - the six things the field reports on (owner, 2026-09-18):
// busway, cable tray, single line, branch, conduits and racks, from the host
// model, installed against remaining, with each status. Totals come from the
// server (utils/trackedBuckets, root-f5); this only lays them out. The rules
// still waiting for the owner are said on the page, never hidden.
import React, { useEffect, useState } from "react";
import { Button, Segmented, Skeleton } from "antd";
import { url, tokenRef } from "api";

// racks.unique: a count, or a bucket like the others ({ elements, ... }).
export const racksUnique = (r) => { const u = r && r.unique; if (u == null) return null; return typeof u === "number" ? u : (u.elements != null ? u.elements : null); };
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
export const ORDER = ["busway", "cableTray", "singleLine", "branch", "conduits", "racks", "junctionBoxes", "pullBoxes"];
// Junction and pull boxes are counted and listed by size (owner, 2026-10-17).
export const BOX_KEYS = ["junctionBoxes", "pullBoxes"];

// A box bucket's sizes, most boxes first, "Size unknown" last.
export function boxSizes(s) {
  return Object.entries((s && s.sizes) || {})
    .sort((a, b) => ((a[0] === "Size unknown") - (b[0] === "Size unknown")) || b[1].elements - a[1].elements || a[0].localeCompare(b[0], undefined, { numeric: true }));
}
const STATUS = [["installed", "Installed"], ["being_installed", "Being installed"], ["procured", "Procured"], ["projected", "Not started"]];
const COLOR = { installed: "#2E7D32", being_installed: "#F2B705", procured: "#1F6FD1", projected: "#CBD5E1" };
const n0 = (v) => Math.round(Number(v) || 0).toLocaleString("en-US");

// Racks are counted, runs are measured: the share installed follows what the category is made of.
export function progressOf(key, s) {
  if (!s) return { total: 0, installed: 0, unit: "" };
  if (key === "racks" || !(s.lengthFt > 0)) {
    const total = s.elements || 0;
    return { total, installed: (s.status && s.status.installed && s.status.installed.n) || 0, unit: "ea" };
  }
  return { total: s.lengthFt, installed: s.installedFt || 0, unit: "ft" };
}

// How the categories are counted, in words (the owner confirmed these defaults for SpaceX on 2026-09-19).
export function ruleNotes(config) {
  if (!config) return [];
  return [
    `1-1/4" and 1-1/2" conduit counted as ${config.midSizeConduit === "singleLine" ? "single line" : "branch"}`,
    `Racks: ${config.racks === "allSupports" ? "every support (SP_*)" : "trapeze supports only (SP_Trapeze_*)"}`,
    `Racks by system: ${config.racksBySystem === "geometry" ? "what each rack carries, from the runs crossing it (a rack counts under each system it carries)" : "not split"}`,
  ];
}

function Bar({ s }) {
  const total = STATUS.reduce((n, [k]) => n + ((s.status && s.status[k] && s.status[k].n) || 0), 0) || 1;
  return (
    <div className="g-tbar" role="img" aria-label={STATUS.map(([k, l]) => `${l} ${(s.status && s.status[k] && s.status[k].n) || 0}`).join(", ")}>
      {STATUS.map(([k]) => {
        const v = (s.status && s.status[k] && s.status[k].n) || 0;
        return v ? <i key={k} style={{ width: `${(100 * v) / total}%`, background: COLOR[k] }} /> : null;
      })}
    </div>
  );
}

function Row({ k, label, s }) {
  const p = progressOf(k, s);
  const pct = p.total ? Math.round((100 * p.installed) / p.total) : 0;
  return (
    <tr>
      <td><b>{label}</b></td>
      <td className="g-num">{n0(s.elements)}</td>
      <td className="g-num">{s.runs ? n0(s.runs) : ""}{s.fittings ? <div className="g-tsub">{n0(s.fittings)} fittings</div> : null}{s.equipment ? <div className="g-tsub">{n0(s.equipment)} equipment</div> : null}{s.supports ? <div className="g-tsub">{n0(s.supports)} supports</div> : null}{s.boxes && !s.runs ? <div className="g-tsub">{n0(s.boxes)} boxes</div> : null}</td>
      <td className="g-num">{p.unit === "ft" ? n0(p.total) : ""}</td>
      <td className="g-num">{n0(p.installed)} {p.unit}</td>
      <td className="g-num">{n0(p.total - p.installed)} {p.unit}</td>
      <td className="g-num">{pct}%</td>
      <td style={{ minWidth: 140 }}><Bar s={s} /></td>
    </tr>
  );
}

const HEAD = (
  <thead><tr><th>Category</th><th>Elements</th><th>Runs</th><th>Length (ft)</th><th>Installed</th><th>Remaining</th><th>Done</th><th>Status</th></tr></thead>
);

export default function TrackedCategories({ projectId, compact = false }) {
  const [by, setBy] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    setData(null); setError("");
    fetch(`${url}/projects/${projectId}/tracked-buckets${by ? `?by=${by}` : ""}`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then((b) => { if (alive) setData(b); })
      .catch((e) => { if (alive) setError(e.message || "Could not load the tracked categories"); });
    return () => { alive = false; };
  }, [projectId, by]);

  if (error) return compact ? null : <div className="g-empty">{error}</div>;
  if (!data) return compact ? null : <Skeleton active paragraph={{ rows: 6 }} />;
  if (data.status !== "ok") return compact ? null : <div className="g-empty">No published model for this project yet, so there is nothing to total.</div>;
  const labels = data.labels || {};
  const keys = ORDER.filter((k) => data.buckets && data.buckets[k] && data.buckets[k].elements > 0);
  if (!keys.length) return compact ? null : <div className="g-empty">The model has none of the tracked categories.</div>;

  return (
    <section className="g-tracked">
      <h2>Tracked categories</h2>
      <p className="g-note">
        From the host model{data.host ? ` (${data.host})` : ""}: what is installed and what remains, by tracking status. Elements with no status count as not started.
      </p>
      <div className="g-tablewrap">
        <table>
          {HEAD}
          <tbody>{keys.map((k) => <Row key={k} k={k} label={labels[k] || k} s={data.buckets[k]} />)}</tbody>
        </table>
      </div>
      <div className="g-legend">{STATUS.map(([k, l]) => <span key={k}><i style={{ background: COLOR[k] }} />{l}</span>)}</div>

      {!compact && data.racks && !data.racks.bySystem && data.racks.note && (
        <p className="g-note"><b>Racks by what they carry:</b> {data.racks.note}.</p>
      )}
      {!compact && data.racks && data.racks.bySystem && (
        <>
          <h3>Racks by what they carry</h3>
          {racksUnique(data.racks) != null && (
            // root-f5 2026-09-19: a rack is counted under EACH system it carries, so the rows overlap.
            <p className="g-tsub">
              {racksUnique(data.racks).toLocaleString()} racks in all. A rack that carries several systems is counted under each of them, so the rows add up to more.
            </p>
          )}
          <div className="g-tablewrap">
            <table>
              {HEAD}
              <tbody>
                {Object.entries(data.racks.bySystem).sort((a, b) => b[1].elements - a[1].elements).map(([sys, s]) => (
                  <Row key={sys} k="racks" label={labels[sys] || (sys === "none" || sys === "null" || !sys ? "Nothing crossing" : sys)} s={s} />
                ))}
              </tbody>
            </table>
          </div>
          {data.racks.note && <p className="g-note">{data.racks.note}</p>}
        </>
      )}

      {!compact && BOX_KEYS.filter((k) => keys.includes(k) && data.buckets[k].sizes).map((k) => (
        <React.Fragment key={`sizes-${k}`}>
          <h3>{labels[k] || k} by size</h3>
          <div className="g-tablewrap">
            <table>
              {HEAD}
              <tbody>{boxSizes(data.buckets[k]).map(([size, s]) => <Row key={size} k={k} label={size} s={s} />)}</tbody>
            </table>
          </div>
        </React.Fragment>
      ))}

      {!compact && (
        <>
          <div className="g-ctrl">
            <span>Break down by</span>
            <Segmented size="small" value={by} onChange={setBy} options={[{ label: "Whole project", value: "" }, { label: "Level", value: "level" }, { label: "Area", value: "area" }]} />
          </div>
          {by && data.groups && Object.keys(data.groups).sort().map((g) => (
            <div key={g}>
              <h3>{g}</h3>
              <div className="g-tablewrap">
                <table>
                  {HEAD}
                  <tbody>{ORDER.filter((k) => data.groups[g][k] && data.groups[g][k].elements > 0).map((k) => <Row key={k} k={k} label={labels[k] || k} s={data.groups[g][k]} />)}</tbody>
                </table>
              </div>
            </div>
          ))}
          <div className="g-note">
            <b>How it is counted:</b> {ruleNotes(data.config).join("; ")}.{data.note ? ` ${data.note}` : ""}
          </div>
        </>
      )}
      {compact && <Button size="small" href={`/documentation/${projectId}?tab=tracked`}>Open the tracked categories</Button>}
    </section>
  );
}
